/**
 * Комментарии сотрудников из CRM в ленте «Чатов»: заметки, которых клиент НЕ видел, — в одной
 * хронологии с сообщениями и звонками.
 *
 * Просьба владельца от 22.10.26: «добавить эти комментарии в чат, но выделить отдельно дизайном,
 * чтобы было понятно, что это комментарии». Отсюда вид «стикер-заметка»: кремовая карточка с
 * пунктирной рамкой по центру ленты (не пузырь сообщения — у тех сплошной фон и хвостик к краю;
 * не звонок — у того белая карточка с тенью), в шапке — «клиент не видит».
 *
 * Данные — `comments` ручек `GET /api/chats/{id}` и `…/history` (комментарии CRM к сделке,
 * приходу клиента в салон и товару сделки). Комментарии подряд за один день лента склеивает в
 * одну карточку (`merge` в `ChatsPage`), здесь одинаковые подряд («ао» × 4) схлопываются в строку
 * с диапазоном времени, длинный текст свёрнут до трёх строк, записи робота приглушены.
 * Фото и видео заметки — миниатюрами под текстом; клик открывает слайдер (`MediaViewer`) по всем
 * файлам комментариев ленты (`all`), не только этой карточки дня, с подписью «что, кто, когда».
 * Задача CRM — той же плашкой, что у товара во вкладке «Договор» (`TaskPill`): цвет статуса CRM,
 * рядом статус словом и чем закрыли.
 * Спеки `docs/specs/2026-10-22-chat-crm-comments-design.md`, `docs/specs/2026-10-22-chat-comment-media-design.md`.
 */
import { Bot, Briefcase, ChevronDown, Shirt, StickyNote, Store } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import { TaskPill } from "@/components/chat/ProductTasks";
import { type MediaItem, MediaThumb, MediaViewer } from "@/components/MediaViewer";
import type { ChatComment } from "@/lib/clientChats";

const hhmm = (iso: string) => iso.slice(11, 16);
const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
/** Сколько строк показываем без раскрытия, когда комментариев за день много. */
const SHOW = 6;
/** Длинный текст (BitrixGPT пишет абзацами) — свёрнут до трёх строк. */
const isLong = (t: string) => t.length > 160 || t.split("\n").length > 3;
/** Разметку Bitrix (`[p]…[/p]`, `[br]`) в ленте не показываем. */
const clean = (t: string) =>
  t
    .replace(/\[br\]/gi, "\n")
    .replace(/\[\/?(p|b|i|u)\]/gi, "")
    .trim();

const KIND_ICON = { deal: Briefcase, visit: Store, item: Shirt } as const;
const KIND_TITLE = { deal: "Комментарий к сделке", visit: "Комментарий к приходу клиента в салон", item: "Комментарий к товару сделки" } as const;

/** Строка карточки: один комментарий или несколько одинаковых подряд от одного человека. */
type Row = {
  key: number;
  kind: ChatComment["kind"];
  auto: boolean;
  user: string | null;
  text: string;
  /** Что именно комментировали — приход такого-то дня в таком-то салоне, товар, задача. */
  ctx: string | null;
  deal_id: number | null;
  times: string[];
  files: MediaItem[];
  task: string | null;
  status: ChatComment["task_status"];
};

/**
 * Предмет комментария словами: сделка с номером, приход (дата, если не в день комментария, и салон),
 * товар, задача CRM. Общий для всей карточки предмет уходит в шапку, у строк остаётся только свой.
 */
function context(c: ChatComment): string | null {
  const parts: string[] = [];
  if (c.kind === "deal" && c.deal_id) parts.push(`сделка ${c.deal_id}`);
  if (c.kind === "visit" && c.visit) {
    parts.push(c.visit.date && c.visit.date.slice(0, 10) !== c.date.slice(0, 10) ? `приход ${ddmm(c.visit.date)}` : "приход");
    if (c.visit.location) parts.push(c.visit.location);
  }
  if (c.kind === "item" && c.item) parts.push(c.item);
  // Задача со статусом — плашкой в строке; без статуса (старое) — словом, как раньше.
  if (c.task && !c.task_status) parts.push(c.task);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Комментарии дня → строки: одинаковые подряд (текст, автор, предмет) — одной строкой с диапазоном времени. */
/** Текст, автор, предмет и файлы комментария — как их показываем в строке и в слайдере. */
function describe(c: ChatComment) {
  // «.» ставят, лишь бы поле не было пустым: у заметки с фото текста нет вовсе.
  let text = /[\p{L}\p{N}]/u.test(c.text) ? clean(c.text) : "";
  let user = c.user;
  // Робот Bitrix подписывается в самом тексте — это автор, а не начало заметки.
  if (c.auto && /^BitrixGPT\b/i.test(text)) {
    user = "BitrixGPT";
    text = text.replace(/^BitrixGPT[\s:—-]*/i, "");
  }
  const ctx = context(c);
  const meta = [user, `${ddmm(c.date)} ${hhmm(c.date)}`, ctx].filter(Boolean).join(" · ");
  const files: MediaItem[] = (c.files ?? []).map((f) => ({ ...f, caption: text || null, meta }));
  return { text, user, ctx, files };
}

function toRows(comments: ChatComment[]): Row[] {
  const rows: Row[] = [];
  for (const c of comments) {
    const { text, user, ctx, files } = describe(c);
    const last = rows[rows.length - 1];
    if (
      last &&
      files.length === 0 &&
      last.files.length === 0 &&
      !c.task_status &&
      !last.status &&
      last.kind === c.kind &&
      last.auto === c.auto &&
      last.user === user &&
      last.ctx === ctx &&
      last.text.toLowerCase() === text.toLowerCase() &&
      !isLong(text)
    ) {
      last.times.push(c.date);
      continue;
    }
    rows.push({
      key: c.id, kind: c.kind, auto: c.auto, user, text, ctx, deal_id: c.deal_id, times: [c.date], files,
      task: c.task_status ? c.task : null, status: c.task_status,
    });
  }
  return rows;
}

/**
 * `feed` — все комментарии ленты: слайдер листает их файлы целиком, от дня к дню, а «закрыта
 * комментарием» находит закрывший. `bare` — только строки, без рамки и шапки: комментарии прихода
 * внутри его карточки (`VisitCard`), там предмет «приход» и так ясен.
 */
export function CommentBubble({ comments, feed = comments, bare = false }: { comments: ChatComment[]; feed?: ChatComment[]; bare?: boolean }) {
  const [all, setAll] = useState(false);
  const [view, setView] = useState<number | null>(null);
  const rows = toRows(comments);
  // У комментария — начало его файлов в общем списке ленты.
  const media: MediaItem[] = [];
  const from = new Map<number, number>();
  for (const c of feed) {
    from.set(c.id, media.length);
    media.push(...describe(c).files);
  }
  const hidden = !all && rows.length > SHOW + 2 ? rows.length - SHOW : 0;
  const shown = hidden ? rows.slice(hidden) : rows;
  const robots = rows.every((r) => r.auto);
  const title = robots
    ? rows.length > 1 ? "Записи робота CRM" : "Запись робота CRM"
    : rows.length > 1 ? "Комментарии сотрудников" : "Комментарий сотрудника";
  // Все строки об одном (та же сделка, тот же приход) — предмет один раз в шапке, строки чище.
  const common = rows.every((r) => r.ctx === rows[0]?.ctx) ? (rows[0]?.ctx ?? null) : null;
  const byId = new Map(feed.map((c) => [c.id, c]));
  const list = shown.map((r, i) => (
    <CommentRow
      key={r.key}
      r={r}
      ctx={bare && r.kind === "visit" ? null : common ? null : r.ctx}
      showUser={i === 0 || shown[i - 1]?.user !== r.user}
      onOpen={(k) => setView((from.get(r.key) ?? 0) + k)}
      closer={r.status?.done_id ? byId.get(r.status.done_id) : undefined}
    />
  ));
  const viewer = view !== null && <MediaViewer items={media} start={view} onClose={() => setView(null)} />;
  if (bare)
    return (
      <div className="flex flex-col gap-1">
        {list}
        {viewer}
      </div>
    );
  return (
    <div
      className="my-1 flex w-[min(94%,560px)] flex-none flex-col gap-1 self-center rounded-[10px] border border-dashed border-[color-mix(in_srgb,var(--warn)_55%,transparent)] bg-warn-bg px-3 py-2 text-[13px] text-[var(--tg-text)]"
      data-annot="комментарий CRM в ленте чата"
    >
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px]">
        <StickyNote size={14} className="flex-none text-warn" />
        <span className="font-medium">{title}</span>
        <span className="text-[var(--tg-sec)]">· клиент не видит</span>
        {common && <span className="ml-auto text-[var(--tg-sec)]">{common}</span>}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="flex items-center gap-1 self-start text-[12px] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
        >
          <ChevronDown size={14} className="rotate-180" />
          ещё {hidden} раньше
        </button>
      )}
      {list}
      {viewer}
    </div>
  );
}

/** Строка: время (или диапазон), значок предмета, текст, сколько раз, предмет, автор — автор только когда сменился. */
function CommentRow({
  r,
  ctx,
  showUser,
  onOpen,
  closer,
}: {
  r: Row;
  ctx: string | null;
  showUser: boolean;
  onOpen: (k: number) => void;
  /** Комментарий, которым закрыли задачу, если он есть в ленте. */
  closer?: ChatComment | undefined;
}) {
  const [open, setOpen] = useState(false);
  const long = isLong(r.text);
  const Icon = r.auto ? Bot : KIND_ICON[r.kind];
  const first = r.times[0] ?? "";
  const last = r.times[r.times.length - 1] ?? first;
  const when = r.times.length > 1 ? `${hhmm(first)}–${hhmm(last)}` : hhmm(first);
  const iconTitle = `${r.auto ? "Запись робота · " : ""}${KIND_TITLE[r.kind]}${r.deal_id ? ` ${r.deal_id} — открыть карточку` : ""}`;
  const icon: ReactNode = <Icon size={13} className="mt-[3px] flex-none" />;
  const tone = r.auto ? "text-[var(--tg-sec)]" : "";
  return (
    <div className="flex items-start gap-x-2">
      <span
        className="min-w-[2.7rem] flex-none text-[12px] tabular-nums text-[var(--tg-sec)]"
        title={r.times.length > 1 ? r.times.map(hhmm).join(", ") : undefined}
      >
        {when}
      </span>
      {r.deal_id ? (
        <Link to={`/deals/${r.deal_id}`} title={iconTitle} className="flex-none text-[var(--tg-sec)] hover:text-[var(--tg-text)]">
          {icon}
        </Link>
      ) : (
        <span title={iconTitle} className="flex-none text-[var(--tg-sec)]">
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {r.task && r.status && <TaskChip task={r.task} status={r.status} closed={!!closer} />}
        {r.text && <span className={`whitespace-pre-wrap ${long && !open ? "line-clamp-3" : ""} ${tone}`}>{r.text}</span>}
        {(ctx || r.times.length > 1 || long) && (
          <span className="text-[12px] text-[var(--tg-sec)]">
            {r.times.length > 1 && <span className="tabular-nums"> ×{r.times.length}</span>}
            {ctx && <>{r.text || r.times.length > 1 ? " · " : ""}{ctx}</>}
            {long && (
              <button type="button" onClick={() => setOpen(!open)} className="ml-1.5 hover:text-[var(--tg-text)]">
                {open ? "свернуть" : "ещё"}
              </button>
            )}
          </span>
        )}
        {r.status?.done && closer && (
          <div className="text-[12px] text-ok" data-annot="чем закрыли задачу">
            ✓ выполнено{r.status.date ? ` ${ddmm(r.status.date)} ${hhmm(r.status.date)}` : ""}
            <span className="text-[var(--tg-sec)]">
              {" "}
              · {closer.user ?? "сотрудник"}: «{clean(closer.text)}»
            </span>
          </div>
        )}
        {r.files.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1" data-annot="фото и видео комментария">
            {r.files.map((f, k) => (
              <MediaThumb key={`${f.url}-${k}`} item={f} onClick={() => onOpen(k)} />
            ))}
          </div>
        )}
      </div>
      {showUser && r.user && <span className="flex-none text-[12px] text-[var(--tg-sec)]">{r.user}</span>}
    </div>
  );
}

/** Цвета статусов задачи — `sTask_status.taskColor` CRM. */
const STATUS_COLOR: Record<number, string> = { 1: "#E74C3C", 2: "#E67E22", 3: "#138D75", 4: "#c2c2a3" };

/**
 * Плашка задачи — та же, что у товара во вкладке «Договор» (`TaskPill`, просьба владельца
 * 22.10.26): цвет статуса CRM, рядом статус словом. Закрытую комментарием — «выполнено» не
 * повторяем, под строкой и так «✓ выполнено … чем закрыли».
 */
function TaskChip({ task, status, closed }: { task: string; status: NonNullable<ChatComment["task_status"]>; closed: boolean }) {
  const when = status.date ? ` · ${ddmm(status.date)} ${hhmm(status.date)}` : "";
  return (
    <span className="mr-1.5 inline-flex items-center gap-x-1.5 align-[1px]" title={`Задача CRM «${task}»: ${status.name.toLowerCase()}${when}`} data-annot="задача комментария">
      <TaskPill name={task} color={STATUS_COLOR[status.id] ?? null} status={closed ? null : status.name} />
    </span>
  );
}
